/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Set, List } from 'immutable';

import { setSortBy, setSortByReversed, setSelectedItems, setSelectedItem } from 'actions/uiActions';
import Table from 'components/Table';
import TableData from './TableData';
import TableHeader from './TableHeader';
import TableDataCheckbox from './TableDataCheckbox';
import TableHeaderSelectAll from './TableHeaderSelectAll';
import ReportPagination from './ReportPagination';

const DEFAULT_PAGE_SIZE = 10;

export class ReportTable extends React.PureComponent {
  setSortBy(path) {
    const { name, sortByReversed, sortBy } = this.props;
    const reverse = path === sortBy && !sortByReversed;
    this.props.setSortBy(name, path);
    this.props.setSortByReversed(name, reverse);
    this.props.sortReportData(path, reverse);
  }

  render() {
    const {
      items, count, page, selectedItems, fields, includePagination, pageSize,
      noItemsMessage, allowItemSelection, sortBy, sortByReversed, name,
    } = this.props;
    const allChecked = items.every(i => (
      selectedItems.includes(i.get('documentid'))
    ));
    return (
      <div className="report-table">
        <Table
          className="padded"
          renderRow={(row, headers) => {
            const checked = selectedItems.includes(row.get('documentid'));
            return (
              <tr key={row.get('documentid')}>
                {headers.map(header => (
                  <TableData
                    key={header.get('name')}
                    row={row}
                    header={header}
                  />
                ))}
                {allowItemSelection ? (
                  <TableDataCheckbox
                    checked={checked}
                    onChange={() => this.props.setSelectedItem(name, row.get('documentid'), !checked)}
                  />
                ) : null}
              </tr>
            );
          }}
          renderHeaders={(headers) => {
            const th = headers.map(header => (
              <TableHeader
                key={header.get('name')}
                header={header}
                sortBy={sortBy}
                setSortBy={sortPath => this.setSortBy(sortPath)}
                sortByReversed={sortByReversed}
              />
            ));
            if (allowItemSelection) {
              return th.push((
                <TableHeaderSelectAll
                  key="select-all"
                  checked={allChecked}
                  onChange={() => this.props.setSelectedItems(name, items.map(i => i.get('documentid')), !allChecked)}
                />
              ));
            }
            return th;
          }}
          rows={items}
          columns={fields}
          emptyMessage={noItemsMessage || 'There are no items to display.'}
          emptyColSpan={fields.size + (allowItemSelection ? 1 : 0)}
        />
        {includePagination ? (
          <ReportPagination
            currentPage={page}
            pageCount={count ? Math.ceil(count / pageSize) : 1}
            onPageChange={pageNumber => this.props.setPage(pageNumber)}
          />
        ) : null}
      </div>
    );
  }
}

ReportTable.propTypes = {
  name: PropTypes.string.isRequired,
  fields: ImmutablePropTypes.list.isRequired,
  selectedItems: ImmutablePropTypes.set,
  items: ImmutablePropTypes.list,
  count: PropTypes.number,
  page: PropTypes.number,
  noItemsMessage: PropTypes.string,
  sortBy: PropTypes.string,
  sortByReversed: PropTypes.bool,
  allowItemSelection: PropTypes.bool,
  includePagination: PropTypes.bool,
  pageSize: PropTypes.number,
  setSortByReversed: PropTypes.func.isRequired,
  setSortBy: PropTypes.func.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
  setSelectedItem: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  sortReportData: PropTypes.func.isRequired,
};

ReportTable.defaultProps = {
  items: List(),
  count: DEFAULT_PAGE_SIZE,
  page: 1,
  noItemsMessage: null,
  sortBy: '',
  sortByReversed: false,
  selectedItems: Set(),
  allowItemSelection: false,
  includePagination: false,
  pageSize: DEFAULT_PAGE_SIZE,
};

export function mapStateToProps(state, props) {
  return {
    sortBy: state.ui.getIn(['sortBy', props.name]),
    sortByReversed: state.ui.getIn(['sortByReversed', props.name]),
    selectedItems: state.ui.getIn(['selectedItems', props.name]),
  };
}

export default connect(
  mapStateToProps,
  { setSortBy, setSortByReversed, setSelectedItems, setSelectedItem },
)(ReportTable);
